import { useEffect, useState } from "react";

import { useTranslations } from "@jti/ui/providers/useTranslations";
import { normalizeDate } from "@jti/ui/utils/formatDate/normalizeDate";

export const MS_PER_MINUTE = 60 * 1000;
export const MS_PER_HOUR = MS_PER_MINUTE * 60;
export const MS_PER_DAY = MS_PER_HOUR * 24;

export type UseLotteryInputType = Date | number | string | null | undefined;

type UseLotteryCountdownResult = {
  label: string;
  isLotteryExpired: boolean;
};

export function useLotteryCountdown(availableTill: UseLotteryInputType): UseLotteryCountdownResult {
  const [label, setLabel] = useState("");
  const [isLotteryExpired, setIsLotteryExpired] = useState(false);
  const { __ } = useTranslations();

  useEffect(() => {
    if (!availableTill) {
      return;
    }

    const rawInput: Date | string =
      availableTill instanceof Date || typeof availableTill === "string" ? availableTill : new Date(availableTill);

    const targetDate = normalizeDate(rawInput);
    const now = new Date();
    const timeRemainingMs = targetDate.getTime() - now.getTime();

    const expired = timeRemainingMs <= 0;
    setIsLotteryExpired(expired);

    let value: number;
    let unit: string;

    if (timeRemainingMs >= MS_PER_DAY) {
      value = Math.floor(timeRemainingMs / MS_PER_DAY);
      unit = __(value === 1 ? "prizes.time.limited.box.day" : "prizes.time.limited.box.days");
    } else if (timeRemainingMs >= MS_PER_HOUR) {
      value = Math.floor(timeRemainingMs / MS_PER_HOUR);
      unit = __(value === 1 ? "prizes.time.limited.box.hour" : "prizes.time.limited.box.hours");
    } else {
      value = Math.floor(timeRemainingMs / MS_PER_MINUTE);
      unit = __(value === 1 ? "prizes.time.limited.box.minute" : "prizes.time.limited.box.minutes");
    }

    if (value < 0) {
      value = 0;
    }

    setLabel(`${value} ${unit}`);
  }, [__, availableTill, isLotteryExpired]);

  return { label, isLotteryExpired };
}
